/**
 * useTearoffBridge — cross-window reattach coordination via BroadcastChannel.
 *
 * Works in both the web app (window.open popups) and the Electron desktop app
 * (separate BrowserWindows share the same session, so BroadcastChannel works).
 *
 * useMainWindowBridge()   — call in the MAIN WebtopPreview to listen for
 *                           reattach requests and Electron IPC reattach events.
 * sendReattach(path,title) — call from the TEAROFF window to ask the main
 *                            window to re-open the view before self-closing.
 */

import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { openWindow } from "../Slices/windowsSlice";

const CHANNEL_NAME = "webtop-bridge";

// Module-level singleton so the channel survives re-renders.
let _channel = null;
function getChannel() {
  if (!_channel && typeof BroadcastChannel !== "undefined") {
    _channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return _channel;
}

export function sendReattach(path, title) {
  getChannel()?.postMessage({ type: "WEBTOP_REATTACH", path, title });
}

export function useMainWindowBridge() {
  const dispatch = useDispatch();

  // BroadcastChannel: works in web popups and across Electron renderer windows
  useEffect(() => {
    const ch = getChannel();
    if (!ch) return;

    const handle = (event) => {
      if (event.data?.type !== "WEBTOP_REATTACH") return;
      const { path, title } = event.data;
      if (path) dispatch(openWindow({ path, title: title || "", deduplicate: true }));
    };

    ch.addEventListener("message", handle);
    return () => ch.removeEventListener("message", handle);
  }, [dispatch]);

  // Electron IPC: main process sends this when a tearoff window is closed (X button)
  useEffect(() => {
    if (!window.electronAPI?.onWebtopReattach) return;
    window.electronAPI.onWebtopReattach(({ path, title }) => {
      if (path) dispatch(openWindow({ path, title: title || "", deduplicate: true }));
    });
  }, [dispatch]);
}
